import { useEffect, useMemo, useRef, useState } from "react";
import { Search, MessageCircleOff, Play, X, ChevronLeft, ChevronRight } from "lucide-react";
import AspirasiCard from "../../Components/Aspirasi/AspirasiCard";
import AspirasiDetailModal from "../../Components/Aspirasi/AspirasiDetailModal";
import { GOOGLE_SHEET_URL } from "../../../utils/googleSheet";
import { getDeviceId } from "../../../utils/deviceId";
import QuestionnairePage from "../Questionnaire/QuestionnairePage";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "./style.css";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const videos = [
  {
    id: "D2cDx3QpjYg",
    title: "Dampak dari Militerisasi Koperasi Desa Merah Putih",
  },
  {
    id: "74c3LXVduks",
    title: "Kementerian Berselisih Merekrut Manajer Koperasi Merah Putih | Bocor Alus Politik",
  },
  {
    id: "yckObUSH_8E",
    title: "Bhima CELIOS: Salah Desain, Koperasi Merah Putih Bisa Picu Masalah Baru",
  },
];

const AUTO_SLIDE = 5000;

// ==== Cache config ====
const CACHE_KEY = "aspirasi_cache_v1";
const CACHE_TTL = 60 * 1000; // 1 menit, sesuaikan kebutuhan
const FETCH_TIMEOUT = 15000; // 15 detik

export interface KomentarItem {
  id: number;
  aspirasi_id: number;
  device_id: string;
  nama: string;
  komentar: string;
  tanggal: string;
  is_owner: boolean;
}

export interface AspirasiItem {
  id: number;
  nama: string;
  no_hp: string;
  alamat: string;
  aspirasi: string;
  foto: string;
  tanggal: string;
  status: string;
  total_like: number;
  liked_by_me: boolean;
  is_owner: boolean;
  komentar: KomentarItem[];
}

interface CacheShape {
  data: AspirasiItem[];
  timestamp: number;
}

const YoutubeHeroEmbed = () => {
  const [current, setCurrent] = useState(0);
  const [open, setOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(videos[0].id);

  const activeVideo = videos[current];

  // Auto Slide
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % videos.length);
    }, AUTO_SLIDE);

    return () => clearInterval(interval);
  }, []);

  // Lock body saat modal
  useEffect(() => {
    if (!open) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  return (
    <>
      <div className="mx-auto max-w-3xl px-5 pt-8">
        <h1 className="mb-8 text-center font-sora text-[26px] font-extrabold text-red-700 sm:text-[32px] md:text-[44px]">
          Podcast Aspirasi
          <br />
          <span className="text-lime-900">Kopdes Merah Putih</span>
        </h1>

        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          slidesPerView={1}
          loop
          centeredSlides
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          pagination={{
            clickable: true,
            el: ".podcast-pagination",
          }}
          navigation={{
            prevEl: ".podcast-prev",
            nextEl: ".podcast-next",
          }}>
          {videos.map((video) => (
            <SwiperSlide key={video.id}>
              <div className="group pb-10">
                <button
                  onClick={() => {
                    setCurrentVideo(video.id);
                    setOpen(true);
                  }}
                  className="overflow-hidden rounded-[26px] bg-white shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
                  <div className="relative">
                    <img
                      alt=""
                      src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                      onError={(e) => {
                        e.currentTarget.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                      }}
                      className="aspect-video w-full object-cover transition duration-500 group-hover:scale-105"
                    />

                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30" />

                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 shadow-xl transition-all duration-300 group-hover:scale-110">
                        <Play size={34} fill="currentColor" className="ml-1 text-lime-600" />
                      </div>
                    </div>
                  </div>

                  <div className="px-6 pt-5 pb-6">
                    <h3 className="line-clamp-2 text-center font-sora text-xl font-bold leading-snug text-slate-800">{video.title}</h3>
                  </div>
                </button>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className=" flex items-center justify-center gap-8 w-full">
          <button className="podcast-prev flex px-2 py-2 items-center justify-center rounded-full border border-lime-300 bg-white text-lime-700 transition hover:bg-lime-600 hover:text-white">
            <ChevronLeft />
          </button>

          <div className="podcast-pagination" />

          <button className="podcast-next flex px-2 py-2 items-center justify-center rounded-full border border-lime-300 bg-white text-lime-700 transition hover:bg-lime-600 hover:text-white">
            <ChevronRight />
          </button>
        </div>
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute -top-12 right-0 rounded-full bg-white/20 p-2 text-white">
              <X />
            </button>

            <div className="aspect-video overflow-hidden rounded-2xl">
              <iframe
                src={`https://www.youtube.com/embed/${currentVideo}?autoplay=1`}
                title={activeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const AspirasiList = () => {
  const [data, setData] = useState<AspirasiItem[]>([]);
  // loading = true hanya kalau memang belum ada data sama sekali untuk ditampilkan
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<AspirasiItem | null>(null);

  const [showAll, setShowAll] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const gridTopRef = useRef<HTMLDivElement>(null);
  const PREVIEW_COUNT = 6;

  // device_id konsisten selama browser/device yang sama dipakai,
  // dipakai buat like, komentar, sekaligus edit/hapus punya sendiri.
  const deviceId = useMemo(() => getDeviceId(), []);

  const readCache = (): CacheShape | null => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as CacheShape;
    } catch {
      // cache korup / gak bisa diparse, anggap gak ada
      return null;
    }
  };

  const writeCache = (items: AspirasiItem[]) => {
    try {
      const payload: CacheShape = { data: items, timestamp: Date.now() };
      localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
    } catch {
      // localStorage penuh / diblokir browser, gak fatal, skip aja
    }
  };

  const fetchData = async (isBackground = false) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      if (isBackground) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // device_id disertakan biar backend bisa kasih tau liked_by_me & is_owner
      const url = `${GOOGLE_SHEET_URL}?device_id=${encodeURIComponent(deviceId)}`;
      const res = await fetch(url, { signal: controller.signal });
      const json = await res.json();
      const newData: AspirasiItem[] = json.data ?? [];

      setData(newData);
      writeCache(newData);

      // kalau modal detail lagi kebuka, sinkronkan juga datanya (misal abis like/komen)
      setSelected((prev) => {
        if (!prev) return prev;
        return newData.find((item) => item.id === prev.id) ?? prev;
      });
    } catch (err: any) {
      if (err?.name === "AbortError") {
        console.error("Fetch aspirasi timeout");
      } else {
        console.error(err);
      }
      // kalau background refresh gagal, data lama (dari cache) tetap dipakai, gak masalah
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const cached = readCache();

    if (cached) {
      // Tampilkan data lama dulu, instant, tanpa skeleton loading
      setData(cached.data);
      setLoading(false);

      const isStale = Date.now() - cached.timestamp > CACHE_TTL;
      if (isStale) {
        fetchData(true); // refresh diam-diam di background
      }
    } else {
      // Belum ada cache sama sekali, fetch normal + tampilkan skeleton
      fetchData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return data.filter((item) => {
      const key = search.toLowerCase();

      return item.nama?.toLowerCase().includes(key) || item.alamat?.toLowerCase().includes(key) || item.aspirasi?.toLowerCase().includes(key);
    });
  }, [data, search]);

  const visibleData = showAll ? filtered : filtered.slice(0, PREVIEW_COUNT);

  const handleToggleShowAll = () => {
    if (!showAll) {
      // klik "Lihat Semua" -> kasih loading dikit dulu sebelum expand
      setExpanding(true);
      setTimeout(() => {
        setShowAll(true);
        setExpanding(false);

        // scroll halus ke atas grid, dikit di atas biar button lama gak nyangkut di posisi sama
        requestAnimationFrame(() => {
          gridTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }, 400); // durasi loading, sesuaikan aja
    } else {
      // klik "Sembunyikan" -> langsung aja, gak perlu loading
      setShowAll(false);
      requestAnimationFrame(() => {
        gridTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  // Update optimis 1 aspirasi di state `data` tanpa perlu re-fetch semua
  // (dipakai abis like/komen biar kerasa instant, lalu tetap fetchData() diam2 buat sinkron)
  //
  // PENTING: cache di localStorage juga HARUS ikut ditulis ulang di sini.
  // Kalau tidak, refresh dalam rentang CACHE_TTL bakal load data lama dari
  // localStorage (tanpa like/komentar yang baru ditambahkan), keliatan
  // seperti "like/komentar hilang" padahal sebenarnya cuma cache yang basi.
  const patchAspirasi = (id: number, patch: Partial<AspirasiItem>) => {
    setData((prev) => {
      const updated = prev.map((item) => (item.id === id ? { ...item, ...patch } : item));
      writeCache(updated); // sinkronkan cache biar refresh gak balik ke data lama
      return updated;
    });
    setSelected((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
  };

  return (
    <>
      <section className="relative overflow-hidden bg-gradient-to-b from-lime-50 via-white to-white py-2 md:py-20">
        {/* Background Blur */}

        <div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-lime-200/40 blur-[140px]" />

        <div className="absolute right-0 top-40 h-80 w-80 rounded-full bg-green-100/40 blur-[130px]" />

        <div className="absolute bottom-0 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-lime-100/30 blur-[120px]" />

        <YoutubeHeroEmbed />
        <div className="relative mx-auto max-w-7xl px-5 mt-20">
          {/* Header */}

          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex rounded-full bg-lime-100 px-5 py-2 text-sm font-semibold text-lime-700">Pusat Aspirasi</span>

            <h2 className="mt-6 text-4xl font-bold text-slate-800 md:text-5xl">Aspirasi Masyarakat</h2>

            <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-500">Setiap aspirasi yang disampaikan merupakan bagian dari upaya bersama untuk membangun Koperasi Desa Merah Putih yang lebih baik.</p>
          </div>

          {/* Search */}

          <div className="mx-auto mt-12 mb-4 max-w-2xl">
            <div
              className="
    flex
    items-center
    rounded-full
    bg-white
    px-6
    py-4
    shadow-xl
    ring-1
    ring-lime-100
  ">
              <Search size={22} className="text-lime-600" />

              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama, alamat atau isi aspirasi..." className="ml-4 w-full bg-transparent outline-none" />
            </div>
          </div>

          {/* Indikator refresh diam-diam di background, opsional & tidak mengganggu */}
          {refreshing && <div className="mb-8 text-center text-xs text-lime-600 animate-pulse">Memperbarui data...</div>}

          {!refreshing && <div className="mb-16" />}

          {/* Loading (hanya muncul kalau benar-benar belum ada data sama sekali) */}

          {loading && (
            <div className="mt-4 grid gap-8 md:grid-cols-2 xl:grid-cols-3 max-h-[70vh]">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-64 rounded-3xl bg-gray-200 animate-pulse" />
              ))}
            </div>
          )}

          {/* Empty */}

          {!loading && filtered.length === 0 && (
            <div className="py-28 text-center">
              <MessageCircleOff size={90} className="mx-auto text-lime-300" />

              <h3 className="mt-8 text-3xl font-bold">Belum Ada Aspirasi</h3>

              <p className="mt-3 text-gray-500">Jadilah orang pertama yang menyampaikan aspirasi.</p>
            </div>
          )}

          {/* Grid */}

          {!loading && filtered.length > 0 && (
            <>
              <div ref={gridTopRef} className={`grid gap-6 md:grid-cols-2 xl:grid-cols-3 ${showAll ? "max-h-[100vh] md:max-h-[200vh] overflow-y-auto" : ""}`}>
                {visibleData.map((item) => (
                  <AspirasiCard key={item.id} data={item} deviceId={deviceId} onClick={() => setSelected(item)} onPatch={(patch) => patchAspirasi(item.id, patch)} />
                ))}
              </div>

              {filtered.length > PREVIEW_COUNT && (
                <div className="mb-10 mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={handleToggleShowAll}
                    disabled={expanding}
                    className="flex items-center gap-2 rounded-full bg-lime-600 px-8 py-3 font-semibold text-white shadow-lg transition hover:bg-lime-700 disabled:opacity-70">
                    {expanding && (
                      <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                    {expanding ? "Memuat..." : showAll ? "Sembunyikan" : `Lihat Semua (${filtered.length})`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <AspirasiDetailModal data={selected} open={selected !== null} deviceId={deviceId} onClose={() => setSelected(null)} onPatch={(patch) => selected && patchAspirasi(selected.id, patch)} />

      {/* <SponsorSection /> */}

      <QuestionnairePage />
    </>
  );
};

export default AspirasiList;
